import React, { useMemo, useState, useEffect, useRef } from "react";

import { ExampleModal } from "./components/ExampleModal";
import { ImageCategories } from "./components/ImageCategories";
import { ImagePreview } from "./components/ImagePreview";

import { IImageStyle } from "data/attach-styles";

import type { IAspectRatioe, ISampleCount } from "./components/ExtraAction";

import CommunityShowcase from "./components/CommunityShowcase";
import { IShowcaseCard } from "./components/ShowcaseCard";

import PromptContainer from "./components/PromptContainer";

import SignInMarketingPopup from "./components/SignInMarketingPopup";
import WelcomeUserPopup from "./components/WelcomeUserPopup";
import UpgradPopup from "./components/UpgradPopup";
import Header from "components/Header";

import { RequestFeatureCard } from "./components/RequestFeatureCard";
import { GenerateImageErrorCard } from "./components/GenerateImageErrorCard";
import { toPascalCase } from "utils/function";

import { getCreditLimits, getActivePlan } from "request/api"; //verifyToken
import defaultConfig from "config";

import {
  getUserCredit,
  saveUserCredit,
  resetGuestCredits,
  getMsUntilMidnight,
  UserCredit,
  removeUserCredit,
} from "utils/creditUtils";

import { showcaseCards } from "data/showcase-data";

import { aspectRatioes } from "data/aspect-ratioes";
import { sampleOptions } from "data/sample-count";
import { imageGenerationCategories } from "data/image-categories";

import { getRandomItems, getRandomValue } from "utils/function";
import { useProcessFiles, type StoredFile } from "hooks/useProcessFiles";
import {
  useGenerateImage,
  type FormDraft,
  Models,
  models,
} from "hooks/useGenerateImage";
import { MixpanelAnalytics } from "utils/mixpanel";
import ReactGA from "react-ga4";

const mobileBreakpoint = 768;
const maxCharLength = 1920;

const ImageCreator: React.FC = () => {
  const {
    selectedFiles,
    processFiles,
    removeFile,
    resetFiles,
    setSelectedFiles,
    updateFile,
    acceptString,
  } = useProcessFiles();

  const [selectedImageStyleIndex, setSelectImageStyleIndex] =
    useState<number>(0);

  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string>("");

  const [selectedItem, setSelectedItem] = useState<IImageStyle[]>([]);

  const [aspectRatio, setAspectRatio] = useState<IAspectRatioe>(
    aspectRatioes[0]
  );

  const [sampleCount, setSampleCount] = useState<ISampleCount>(
    sampleOptions[2]
  );

  const [showcases, setShowcases] = useState<IShowcaseCard[]>(
    getRandomItems(showcaseCards, 3, [])
  );

  const [onlineCount, setOnlineCount] = useState<number>(getRandomValue());

  const [isMobile, setIsMobile] = useState<boolean>(false);

  const [creditInfo, setCreditInfo] = useState<UserCredit>(getUserCredit());
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showWUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isNeedReload, setIsNeedReload] = useState<boolean>(false);

  const [isCompleted, setIsCompleted] = useState(false);
  const hasHandled = useRef(false);

  const [model, setModel] = useState<Models>(models.image_generation);

  // 输入框
  const promptContainerRef = useRef<HTMLDivElement>(null);

  const userCredits = useMemo((): number => {
    const newCredit =
      creditInfo.role === "admin"
        ? creditInfo.credits
        : Math.max(0, creditInfo.daily_limit - creditInfo.used_today);
    return newCredit;
  }, [
    creditInfo.role,
    creditInfo.credits,
    creditInfo.daily_limit,
    creditInfo.used_today,
  ]);

  // Compute useMessage using useMemo
  const useMessage = useMemo((): string => {
    const prompt = message;

    // Compute styleString from selectedItem
    let styleString = "";
    if (selectedItem.length > 0) {
      const selectedAttachStyles = selectedItem.map((item) => item.value);
      styleString = `, ${selectedAttachStyles.join(",")}`;
    }

    // Combine prompt, aspect ratio, and styleString
    const combinedMessage = `${prompt}${styleString}`;

    // // Ensure the combined message respects maxCharLength
    // if (combinedMessage.length > maxCharLength) {
    //   console.warn("Message exceeds maxCharLength, truncating...");
    //   return combinedMessage.slice(0, maxCharLength);
    // }

    return combinedMessage;
  }, [message, selectedItem]);

  // 获取用户credits
  const onGetUserCredits = () => {
    if (creditInfo.token) {
      if (creditInfo.role !== "admin") {
        getCreditLimits()
          .then((res: any) => {
            const updatedCredit = {
              ...creditInfo,
              isNeedToShowWelcomePopop: false,
              daily_limit: res?.daily_limit,
              used_today: res?.used_today,
            };
            saveUserCredit(updatedCredit);
            setCreditInfo(updatedCredit);
          })
          .catch((err: any) => {
            const status = err?.status;
            if (status === 401) {
              handleLogout();
            }
          });
      } else {
        getActivePlan()
          .then((res: any) => {
            const updatedCredit = {
              ...creditInfo,
              isNeedToShowWelcomePopop: false,
              credits: res.credits,
            };
            saveUserCredit(updatedCredit);
            setCreditInfo(updatedCredit);
          })
          .catch((err: any) => {
            const status = err?.status;
            if (status === 401) {
              handleLogout();
            }
          });
      }
    }
  };

  // 滚动到底部
  const handleScroll = (): void => {
    if (isMobile) {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth" as ScrollBehavior,
      });
    }
  };

  // Compute isOverMaxCharLength using useMemo for real-time response
  const isOverMaxCharLength = useMemo((): boolean => {
    return useMessage.length > maxCharLength;
  }, [useMessage]);

  const {
    generationImages,
    isEditingImage,
    fetchErrorMessage,
    disabled,
    isLoading,
    setGenerationImages,
    onGenerateImage,
    onTestGenerateImageFetch,
    resetErrorMessage,
  } = useGenerateImage({
    userCredits,
    selectedFiles,
    creditInfo,
    useMessage,
    sampleCount,
    message,
    isOverMaxCharLength,
    selectedItem,
    selectedImageStyleIndex,
    aspectRatio,
    model,
    setModel,
    onGetUserCredits,
    handleScroll,
    setShowLoginModal,
    setShowUpgradeModal,
  });

  // 退出
  const handleLogout = () => {
    removeUserCredit();
    resetGuestCredits();
    setCreditInfo(getUserCredit());
  };

  // 关闭欢迎弹框
  const handleCloseWelcome = () => {
    setShowWelcomeModal(false);
    const newCreditInfo = { ...creditInfo, isNeedToShowWelcomePopop: false };
    setCreditInfo(newCreditInfo);
    saveUserCredit(newCreditInfo);
  };

  // 社区Case刷新
  const hanldeRefresh = () => {
    setOnlineCount(getRandomValue());
    setShowcases((prev) => [...getRandomItems(showcaseCards, 3, prev)]);
    MixpanelAnalytics.trackEvent("community_engage", {
      type: "reload",
      tool: "image-gen",
    });
  };

  useEffect(() => {
    // 立即检查一次
    resetGuestCredits();
    setCreditInfo(getUserCredit());

    // 设置到下次午夜的定时器
    const setMidnightReset = () => {
      const msUntilMidnight = getMsUntilMidnight();

      const timeoutId = setTimeout(() => {
        resetGuestCredits();
        setCreditInfo(getUserCredit());
        setMidnightReset(); // 设置下一次重置
      }, msUntilMidnight);

      return () => clearTimeout(timeoutId);
    };

    return setMidnightReset();
  }, []);

  // 检查窗口大小并更新状态
  useEffect(() => {
    const checkWindowSize = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint);
    };

    // 初次加载时检查
    checkWindowSize();

    // 监听窗口大小变化
    window.addEventListener("resize", checkWindowSize);

    // 清理监听器
    return () => window.removeEventListener("resize", checkWindowSize);
  }, []); // 依赖数组为空，因为 mobileBreakpoint 是常量

  // 接收输入框的值
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setMessage(e.target.value);
  };

  // Enter 提交
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!event.shiftKey && event.key === "Enter") {
      event.preventDefault();
      if (disabled) return;
      onGenerateImage();
    }
  };

  // 选中某个example
  const onSelectedExample = (content: string) => {
    setIsOpen(false);
    setMessage(content);
    MixpanelAnalytics.trackEvent("community_engage", {
      type: "copy",
      tool: "image-gen",
    });
  };

  // 点击时滚动到容器位置
  const handleScrollToContainer = (): void => {
    if (promptContainerRef.current) {
      //isMobile
      const topPosition =
        promptContainerRef.current.getBoundingClientRect().top +
        window.pageYOffset;
      const targetPosition = topPosition - 40;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo({
        top: Math.min(targetPosition, maxScroll), // 确保不超过页面底部
        behavior: "smooth" as ScrollBehavior,
      });
    }
  };
  // 选中卡片上的某个例子
  const handleSelectedCard = async (card: IShowcaseCard) => {
    setMessage(card.prompt);
    // const storedFile = await createStoredFileFromUrl(card.image);
    // setSelectedFiles({ [storedFile.id]: { ...storedFile } });
    resetFiles();
    handleScrollToContainer();
  };

  // 编辑已经生成的图像
  const handleRemix = (storedFile: StoredFile) => {
    updateFile(storedFile);
    // setMessage(generationImages?.prompt || "");
    setGenerationImages(
      (prev) => (prev && { ...prev, editFileId: storedFile.id }) || undefined
    );
    handleScrollToContainer();
    MixpanelAnalytics.trackEvent("image_edit", {
      category: imageGenerationCategories[selectedImageStyleIndex].name,
      aspect_ratio: aspectRatio.value,
      model_id: model,
    });
  };

  // 下载图像
  const onDownloadImage = async (imageUrl: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "downloaded-image.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      MixpanelAnalytics.trackEvent("image_download", {
        category: imageGenerationCategories[selectedImageStyleIndex].name,
        aspect_ratio: aspectRatio.value,
        model_id: model,
      });
    } catch (error) {
      console.error("下载失败:", error);
    } finally {
    }
  };

  // 全屏查看已经生成的图像
  const onVisibleChange = () => {
    MixpanelAnalytics.trackEvent("image_full_view", {
      category: imageGenerationCategories[selectedImageStyleIndex].name,
      aspect_ratio: aspectRatio.value,
      model_id: model,
    });
  };

  // 移除选中的文件后的回调函数 如果是生成的图像，则移除编辑状态
  const handleRemovedCallback = (fileId: string) => {
    if (generationImages?.editFileId === fileId) {
      setGenerationImages(
        (prev) => (prev && { ...prev, editFileId: undefined }) || undefined
      );
    }
  };

  const handleUpgrade = () => {
    const queryString = localStorage.getItem("userInfo") || "{}";
    window.open(
      `${
        defaultConfig.autoLogin_url
      }?origin=imageTool&data=${encodeURIComponent(queryString)}`,
      "_blank"
    );
    setShowUpgradeModal(false);

    MixpanelAnalytics.trackEvent("plan_upgrade_interested", {
      category: imageGenerationCategories[selectedImageStyleIndex].name,
      tool: "image-gen",
    });

    const params: any = {
      action: "plan_upgrade_interested",
      category: toPascalCase(
        imageGenerationCategories[selectedImageStyleIndex].name
      ),
      tool: "image-gen",
    };

    ReactGA.event(params);
  };

  useEffect(() => {
    if (creditInfo.token && creditInfo.isNeedToShowWelcomePopop) {
      setShowWelcomeModal(true);
    }
    // eslint-disable-next-line
  }, [creditInfo.isNeedToShowWelcomePopop]);

  useEffect(() => {
    onGetUserCredits();

    // eslint-disable-next-line
  }, [creditInfo.token]);

  useEffect(() => {
    if (isOpen) {
      MixpanelAnalytics.trackEvent("template_used", {
        category: imageGenerationCategories[selectedImageStyleIndex].name,
        tool: "image-gen",
      });
    }
  }, [isOpen, selectedImageStyleIndex]);

  useEffect(() => {
    if (
      isCompleted &&
      !hasHandled.current &&
      creditInfo.isNeedToShowWelcomePopop === false
    ) {
      onGenerateImage(true);
      hasHandled.current = true;
    }
  }, [
    isCompleted,
    message,
    sampleCount,
    selectedItem,
    aspectRatio,
    selectedImageStyleIndex,
    selectedFiles,
    creditInfo.isNeedToShowWelcomePopop,
    onGenerateImage,
  ]);

  // 从 localStorage 恢复数据
  useEffect(() => {
    hasHandled.current = false;
    const savedData = localStorage.getItem("formDraft");
    if (savedData) {
      try {
        const parsedData: FormDraft = JSON.parse(savedData);
        setMessage(parsedData.message);

        if (parsedData?.selectedFiles) {
          setSelectedFiles(parsedData?.selectedFiles);
        }
        if (parsedData.selectedItem && parsedData.selectedItem.length > 0) {
          setSelectedItem(parsedData.selectedItem);
          setIsNeedReload(true);
        }

        if (parsedData?.aspectRatio) {
          setAspectRatio(parsedData?.aspectRatio);
        }

        if (parsedData?.sampleCount) {
          setSampleCount(parsedData?.sampleCount);
        }

        if (parsedData?.selectedImageStyleIndex !== undefined) {
          setSelectImageStyleIndex(parsedData.selectedImageStyleIndex);
        }

        if (parsedData?.isStorageCompleted && creditInfo.token) {
          setIsCompleted(true);
        }

        localStorage.removeItem("formDraft");
      } catch (error) {
        console.error("Error restoring form data:", error);
        localStorage.removeItem("formDraft");
      }
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SignInMarketingPopup
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      <WelcomeUserPopup
        userName={creditInfo.nickname}
        onStartCreating={handleCloseWelcome}
        open={showWelcomeModal}
        onClose={handleCloseWelcome}
      />
      <ExampleModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onSelected={onSelectedExample}
        selectedImageStyleIndex={selectedImageStyleIndex}
      />
      <UpgradPopup
        open={showWUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />
      <div className="flex flex-col items-center justify-center w-full">
        {/* Header */}
        <Header userCredits={userCredits} creditInfo={creditInfo} />

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row md:space-x-4 py-2 px-3 md:px-auto mx-2 max-w-[calc(100vw-10px)] lg:w-[98%] xl:w-4/5 lg:max-w-[1600px]">
          <div className="w-full lg:w-2/5">
            <ImageCategories
              selectedImageStyleIndex={selectedImageStyleIndex}
              setSelectImageStyleIndex={setSelectImageStyleIndex}
            />

            <PromptContainer
              promptContainerRef={promptContainerRef}
              selectedImageStyleIndex={selectedImageStyleIndex}
              selectedFiles={selectedFiles}
              message={message}
              isOpen={isOpen}
              selectedItem={selectedItem}
              aspectRatio={aspectRatio}
              sampleCount={sampleCount}
              isLoading={isLoading}
              fetchErrorMessage={fetchErrorMessage}
              disabled={disabled}
              isOverMaxCharLength={isOverMaxCharLength}
              acceptString={acceptString}
              isEditingImage={isEditingImage}
              isNeedReload={isNeedReload}
              setIsNeedReload={setIsNeedReload}
              setSelectedItem={setSelectedItem}
              setAspectRatio={setAspectRatio}
              setSampleCount={setSampleCount}
              setIsOpen={setIsOpen}
              setSelectedFiles={setSelectedFiles}
              onRemovedCallback={handleRemovedCallback}
              handleChange={handleChange}
              handleKeyDown={handleKeyDown}
              onFetch={() => onGenerateImage()}
              onTestFetch={onTestGenerateImageFetch}
              processFiles={processFiles}
              removeFile={removeFile}
              onTestUpgrade={() => {
                if (creditInfo.token) {
                  setShowUpgradeModal(true);
                }
              }}
            />
          </div>

          {/* Right Content */}
          <div className="w-full lg:w-3/5 h-fit">
            {fetchErrorMessage ? (
              <GenerateImageErrorCard
                fetchErrorMessage={fetchErrorMessage}
                resetErrorMessage={resetErrorMessage}
                onUpgrade={handleUpgrade}
                onGenerateImage={onGenerateImage}
              />
            ) : (
              <>
                {generationImages || isLoading ? (
                  <ImagePreview
                    generationImages={generationImages}
                    isLoading={isLoading}
                    setSelectedFiles={setSelectedFiles}
                    onRemix={handleRemix}
                    onDownloadImage={onDownloadImage}
                    onVisibleChange={onVisibleChange}
                  />
                ) : (
                  <CommunityShowcase
                    data={showcases}
                    onSelected={handleSelectedCard}
                    onlineCount={onlineCount}
                    onRefreshOnlineCount={hanldeRefresh}
                  />
                )}

                {/* request feature Tips */}
                <RequestFeatureCard />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCreator;
